"use client";

import type { ReactNode } from "react";
import { ArrowLeft } from "lucide-react";
import { useRouter } from "next/navigation";

import { CartProvider, useCart } from "@/components/cart/cart-provider";
import { BottomNavigation, type BottomNavigationActiveItem } from "@/components/catalog/bottom-navigation";
import { PreviewReadOnlyNotice } from "@/components/catalog/preview-read-only-notice";
import { CatalogSearchForm } from "@/components/catalog/catalog-search-form";
import { TopAppBar } from "@/components/catalog/top-app-bar";
import { isEcommercePreviewReadOnly } from "@/config/preview-mode";
import { MotionSection } from "@/components/ui/motion-primitives";
import { FloatingWhatsAppButton } from "@/components/whatsapp/floating-whatsapp-button";

export type CatalogShellHeader = "top-app-bar" | "search" | "none";

export interface CatalogShellProps {
  readonly activeItem: BottomNavigationActiveItem;
  readonly children: ReactNode;
  readonly header?: CatalogShellHeader;
  readonly searchDefaultValue?: string;
  readonly showHeader?: boolean;
  readonly whatsappHref?: string;
}

export function CatalogShell({
  activeItem,
  children,
  header,
  searchDefaultValue,
  showHeader,
  whatsappHref,
}: Readonly<CatalogShellProps>) {
  const resolvedHeader = resolveHeader(header, showHeader);

  return (
    <CartProvider>
      <CatalogShellChrome
        activeItem={activeItem}
        header={resolvedHeader}
        searchDefaultValue={searchDefaultValue}
        whatsappHref={whatsappHref}
      >
        {children}
      </CatalogShellChrome>
    </CartProvider>
  );
}

interface CatalogShellChromeProps {
  readonly activeItem: BottomNavigationActiveItem;
  readonly children: ReactNode;
  readonly header: CatalogShellHeader;
  readonly searchDefaultValue?: string;
  readonly whatsappHref?: string;
}

function CatalogShellChrome({
  activeItem,
  children,
  header,
  searchDefaultValue,
  whatsappHref,
}: Readonly<CatalogShellChromeProps>) {
  const { totals } = useCart();
  const isReadOnlyPreview = isEcommercePreviewReadOnly();

  return (
    <>
      <PreviewReadOnlyNotice />
      <CatalogShellHeaderBar
        activeItem={activeItem}
        header={header}
        searchDefaultValue={searchDefaultValue}
      />
      <MotionSection>{children}</MotionSection>
      {whatsappHref && !isReadOnlyPreview ? (
        <FloatingWhatsAppButton href={whatsappHref} />
      ) : null}
      <BottomNavigation activeItem={activeItem} cartCount={totals.itemCount} />
    </>
  );
}

interface CatalogShellHeaderBarProps {
  readonly header: CatalogShellHeader;
  readonly searchDefaultValue?: string;
  readonly activeItem?: BottomNavigationActiveItem;
}

function CatalogShellHeaderBar({
  header,
  searchDefaultValue,
  activeItem,
}: Readonly<CatalogShellHeaderBarProps>) {
  const router = useRouter();
  const { totals } = useCart();

  if (header === "none") {
    return null;
  }

  return (
    <>
      {/* En desktop, mostramos siempre el TopAppBar con buscador y navegación integrados */}
      <div className="hidden md:block">
        <TopAppBar
          cartCount={totals.itemCount}
          activeItem={activeItem}
          searchDefaultValue={searchDefaultValue}
        />
      </div>

      {/* En movil, mantenemos el comportamiento anterior */}
      <div className="md:hidden">
        {header === "top-app-bar" ? (
          <TopAppBar cartCount={totals.itemCount} activeItem={activeItem} />
        ) : (
          <header className="sticky top-0 z-40 bg-background px-4 py-3 shadow-sm">
            <div className="mx-auto flex w-full max-w-md items-center gap-2">
              <button
                aria-label="Volver"
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-primary outline-none transition-colors hover:bg-primary/5 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                onClick={() => {
                  if (window.history.length > 1) {
                    router.back();
                    return;
                  }

                  router.push("/");
                }}
                type="button"
              >
                <ArrowLeft aria-hidden="true" size={24} strokeWidth={2.4} />
              </button>
              <CatalogSearchForm
                className="min-w-0 flex-1"
                defaultValue={searchDefaultValue}
              />
            </div>
          </header>
        )}
      </div>
    </>
  );
}

function resolveHeader(
  header: CatalogShellHeader | undefined,
  showHeader: boolean | undefined,
): CatalogShellHeader {
  if (header) {
    return header;
  }

  if (showHeader === false) {
    return "none";
  }

  return "search";
}
